import React from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ContentView from './ContentView/ContentView';
import DevView from './DevView';
import GradeSyncView from './GradeSyncView';
import LangfuseView from './LangfuseView';
import SupportView from './SupportView';
import SysadminWelcome from './SysadminWelcome';
import { SPANav, SPANavProvider } from './shared/SPANav';
import SPANotFound from './shared/SPANotFound';
import { SPAUserProvider } from './shared/SPAUser';

export interface SysadminSPAProps {
  canManageContent?: boolean;
  canManageSupport?: boolean;
  canViewSyncLog?: boolean;
  showPrintTools?: boolean;
  showSearch?: boolean;
  signOutPath: string;
  supportPath: string;
  userName: string;
}

// AIDEV-NOTE: Sysadmin SPA shell. Content, Support, and Dev views render client-side without full
// page reloads; the legacy Support pages other than User Search still open via <a href>.
// Auto-redirect from /admin controlled by use_beta_sysadmin_nav flag; /sysadmin always accessible to sysadmins.
//
// No top bar anywhere: the rail (SPANav) is the whole chrome — nav tree, logo, theme, and
// account all live in it — and rail pages get the full viewport height. The welcome page stays
// rail-less on purpose (its cards are the tree's first level at full size) and absorbs the
// chrome itself: hero logo plus a bottom-left theme/account cluster.
const SysadminSPAInner = ({
  canManageContent = true,
  canManageSupport = true,
  canViewSyncLog = false,
  showPrintTools = false,
  showSearch = false,
  signOutPath,
  supportPath,
  userName,
}: SysadminSPAProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const routes = (
    <Routes>
      <Route
        element={(
          <SysadminWelcome
            signOutPath={signOutPath}
            userName={userName}
            onNavigate={navigate}
          />
        )}
        path="/"
      />
      <Route element={<ContentView />} path="/content/*" />
      <Route element={<SupportView supportPath={supportPath} />} path="/support/*" />
      <Route element={<DevView />} path="/dev/*" />
      <Route element={<GradeSyncView />} path="/support/grade-sync/*" />
      <Route element={<LangfuseView />} path="/langfuse/*" />
      <Route element={<SPANotFound homeLabel="Back to home" homeTo="/" />} path="*" />
    </Routes>
  );

  return (
    <SPAUserProvider value={{
      canManageContent, canManageSupport, canViewSyncLog, showPrintTools, showSearch,
    }}
    >
      <SPANavProvider>
        {pathname === '/' ? routes : (
          <div className="tw-flex tw-h-screen tw-overflow-hidden tw-bg-spa-bg">
            <SPANav signOutPath={signOutPath} supportPath={supportPath} userName={userName} />
            {/* scrollbar-gutter: a short page must not be a scrollbar's width wider than a long one. */}
            <main className="tw-min-w-0 tw-flex-1 tw-overflow-y-auto [scrollbar-gutter:stable]">{routes}</main>
          </div>
        )}
      </SPANavProvider>
    </SPAUserProvider>
  );
};

export default SysadminSPAInner;
